/**
 * Defers the header's navigation to the in-page category bar.
 *
 * On the homepage the same links appear twice: once in the header, once in the
 * `.khimuli-category-bar` section. Showing both at the same moment reads as a
 * duplicated menu, so the header's copy steps aside while the in-page bar is on
 * screen and comes back once it scrolls away.
 *
 * The visibility itself is CSS. This file only publishes two attributes on the
 * header for that CSS to key off:
 *
 *   data-khimuli-nav-managed   present once this script has taken over
 *   data-khimuli-inpage-nav    "visible" | "hidden" — is the bar on screen
 *
 * The `managed` attribute is what makes this progressive: without JavaScript
 * neither attribute is ever set, no rule matches, and the header nav simply
 * stays visible the way it does on every other page.
 *
 * Only the homepage is managed. Every other template has no in-page bar to
 * defer to, so its header nav is always present.
 */

const BAR_SELECTOR = '.khimuli-category-bar';
const HEADER_SELECTOR = 'header-component.header';

/**
 * How much of the bar has to be showing before the header stands down. A bar
 * with four pixels poking over the fold is not yet doing its job, and at the
 * default viewport its top edge lands almost exactly on the fold — so anything
 * keyed off "any overlap at all" flickers there.
 */
const ON_SCREEN_RATIO = 0.15;

/**
 * How much of `rect` is inside the viewport, 0 to 1. Mirrors what
 * IntersectionObserver reports as intersectionRatio, so the value used before
 * the observer's first callback agrees with every value after it.
 *
 * @param {DOMRect} rect
 * @returns {number}
 */
function visibleRatio(rect) {
  if (rect.height === 0) return 0;
  const visible = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);
  return Math.max(0, visible) / rect.height;
}

/** @type {IntersectionObserver | null} */
let observer = null;

/**
 * The header is the only element the CSS reads, so state lives there rather
 * than on the document — a section re-render in the theme editor replaces the
 * header and takes its stale attributes with it.
 *
 * @param {Element} header
 * @param {boolean} barOnScreen
 */
function publish(header, barOnScreen) {
  header.dataset.khimuliInpageNav = barOnScreen ? 'visible' : 'hidden';
}

function init() {
  // `template-index` is written onto <body> by layout/theme.liquid.
  if (!document.body.classList.contains('template-index')) return;

  const header = document.querySelector(HEADER_SELECTOR);
  if (!header) return;

  observer?.disconnect();
  observer = null;

  header.dataset.khimuliNavManaged = '';

  const bar = document.querySelector(BAR_SELECTOR);

  // No bar on the page means nothing to defer to, so the header nav is free to
  // show for the whole scroll — the sticky condition in CSS still applies.
  if (!bar) {
    publish(header, false);
    return;
  }

  // Start from the truth for the current scroll position rather than assuming
  // the top of the page: a reload partway down would otherwise flash the nav.
  publish(header, visibleRatio(bar.getBoundingClientRect()) >= ON_SCREEN_RATIO);

  observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        // Deliberately the ratio, not `isIntersecting` — that flag is true for
        // a single overlapping pixel, so it would ignore the threshold below
        // and treat a barely-peeking bar as fully on screen.
        publish(header, entry.intersectionRatio >= ON_SCREEN_RATIO);
      }
    },
    // Both edges, so the callback fires on the way in and on the way out.
    { threshold: [0, ON_SCREEN_RATIO] }
  );

  observer.observe(bar);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

// The theme editor swaps whole sections in and out. Either the header or the
// category bar can be the one replaced, so rebind on any section event.
document.addEventListener('shopify:section:load', init);
document.addEventListener('shopify:section:unload', init);
document.addEventListener('shopify:section:reorder', init);
